import { ComponentPropsWithoutRef, useRef } from "react";
import * as styles from "./HeroPrimary.css";
import { Heading } from "../elements/Heading";
import { HeroPrimary as THeroPrimary } from "~/sanity/types";
import { IImage, Image } from "../elements/Image";
import gsap from "gsap";
import ScrollTrigger from "gsap/dist/ScrollTrigger";
import { SplitText } from "gsap/SplitText";
import { useGSAP } from "@gsap/react";

export type HeroPrimaryProps = ComponentPropsWithoutRef<"div"> & {
  data: THeroPrimary;
};

export const HeroPrimary = ({ data }: HeroPrimaryProps) => {
  const { title, image } = data;

  const heroRef = useRef(null);
  const heroImageRef = useRef(null);
  const triangleBottomLeftRef = useRef(null);
  const triangleRightRef = useRef(null);
  const rectangleTopRef = useRef(null);
  const rectangleLeftRef = useRef(null);

  useGSAP(() => {
    gsap.registerPlugin(ScrollTrigger, SplitText);

    // Initial state for triangles
    gsap.set(triangleBottomLeftRef.current, { height: "25%" });
    gsap.set(triangleRightRef.current, { width: "20%" });

    const headingEl = document.getElementById("hero-primary-heading");
    let headingSplit: ReturnType<typeof SplitText.create> | undefined;

    if (headingEl) {
      headingSplit = SplitText.create(headingEl, {
        type: "lines",
        mask: "lines",
      });
      const lines = headingSplit.lines;
      gsap.set(lines, { y: "100%" });
      gsap.set(headingEl, { visibility: "visible" });

      const tlLoad = gsap.timeline();
      tlLoad
        .fromTo(
          heroImageRef.current,
          { opacity: 0 },
          { opacity: 1, duration: 0.5, ease: "power1.in", delay: 0.5 },
        )
        .fromTo(
          triangleBottomLeftRef.current,
          { height: "25%" },
          { height: "50%", duration: 1, ease: "power1.out" },
          "<",
        )
        .fromTo(
          triangleRightRef.current,
          { width: "20%" },
          { width: "35%", duration: 1, ease: "power1.out" },
          "<",
        )
        .to(
          rectangleTopRef.current,
          { height: 0, duration: 1, ease: "power1.out" },
          "<",
        )
        .to(
          rectangleLeftRef.current,
          { width: 0, duration: 1, ease: "power1.out" },
          "<",
        )
        .fromTo(
          lines,
          { y: "100%" },
          {
            y: 0,
            stagger: {
              amount: 0.2,
              ease: "power1.out",
            },
            delay: 0.3,
            onComplete: () => {
              headingSplit?.revert();
              headingSplit = undefined;
              gsap.set(headingEl, { overflow: "visible" });
            },
          },
          "<",
        );
    }

    const tl = gsap.timeline({
      scrollTrigger: {
        trigger: heroRef.current,
        start: "top top",
        end: "bottom top",
        scrub: true,
        // markers: true,
      },
    });

    tl.to(heroImageRef.current, {
      yPercent: 10,
    });
  }, []);

  return (
    <header className={styles.hero} ref={heroRef}>
      <div className={styles.wrapper}>
        <div className={styles.content}>
          <Heading
            as="h1"
            size="jumbo"
            uppercase
            id="hero-primary-heading"
            className={styles.headingHidden}
          >
            {title}
          </Heading>
        </div>
        {image?.asset && (
          <div className={styles.imageContainer}>
            <div className={styles.imageWrapper} ref={heroImageRef}>
              <Image
                image={image as IImage}
                className={styles.image}
                width={1920}
                loading="eager"
                fetchpriority="high"
              />
            </div>
          </div>
        )}
        <div className={styles.trianglesOverlay} aria-hidden>
          <div className={styles.rectangleTop} ref={rectangleTopRef}></div>
          <div className={styles.rectangleLeft} ref={rectangleLeftRef}></div>
          <div
            className={styles.triangleBottomLeft}
            ref={triangleBottomLeftRef}
          />
          <div className={styles.triangleRight} ref={triangleRightRef} />
        </div>
      </div>
    </header>
  );
};
